// Ubicación en el repo: src/lib/imageCompression.js
// Comprimir fotos en el navegador antes de subirlas a Storage — ver
// DECISIONS.md #38 (consulta del usuario sobre espacio en el plan gratuito
// de Supabase: el límite de 10 MB por archivo no evita que el uso
// acumulado llene el 1 GB disponible con el uso normal de varias
// sucursales). Una foto de celular sin comprimir suele pesar 3-8 MB; acá se
// redimensiona a un máximo razonable y se recomprime como JPEG, bajando el
// peso típico ~10x sin perder legibilidad para un documento escaneado.
//
// Solo aplica a los formatos que `canvas` puede decodificar en cualquier
// navegador (jpeg/png/webp) — PDF y HEIC pasan sin tocar (HEIC no lo
// decodifica canvas de forma confiable fuera de Safari).
const COMPRESSIBLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.82;
const SKIP_COMPRESSION_UNDER_BYTES = 500 * 1024; // ya es chica, no vale la pena

// `opts` permite variantes sin duplicar la función completa — usado por
// branchLogos.js (ver DECISIONS.md #39) con un tope de dimensión menor
// (512px, un logo no necesita 1920px) y `preserveType: true` para no
// forzar PNG→JPEG y perder la transparencia del fondo de un logo.
export async function compressImageIfNeeded(file, opts = {}) {
  const maxDimension = opts.maxDimension ?? MAX_DIMENSION;
  const jpegQuality = opts.jpegQuality ?? JPEG_QUALITY;
  const skipUnderBytes = opts.skipUnderBytes ?? SKIP_COMPRESSION_UNDER_BYTES;
  const preserveType = opts.preserveType ?? false;

  if (!COMPRESSIBLE_TYPES.includes(file.type)) return file;
  if (file.size <= skipUnderBytes) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // si el navegador no puede decodificarla, se sube tal cual
  }

  let { width, height } = bitmap;
  if (width > maxDimension || height > maxDimension) {
    const scale = maxDimension / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const keepPng = preserveType && file.type === 'image/png';
  const outType = keepPng ? 'image/png' : 'image/jpeg';
  const blob = await new Promise((resolve) =>
    keepPng ? canvas.toBlob(resolve, 'image/png') : canvas.toBlob(resolve, 'image/jpeg', jpegQuality)
  );
  if (!blob || blob.size >= file.size) return file; // la compresión no ayudó, usar el original

  const newExt = keepPng ? '.png' : '.jpg';
  const newName = file.name.replace(/\.[^./]+$/, '') + newExt;
  return new File([blob], newName, { type: outType });
}
